import { useEffect, useState, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { passwordProblem } from '../lib/password-reset';
import { IsianSandi } from '../components/IsianSandi';

/**
 * Menyetel kata sandi baru setelah membuka tautan pemulihan.
 *
 * Layar ini SENGAJA tidak meminta kata sandi lama. Orang yang sampai ke sini justru tidak
 * mengetahuinya — itulah alasan ia ada di sini. Yang membuktikan haknya adalah tautan di emailnya,
 * dan pembuktian itu sudah dikerjakan Supabase sebelum halaman ini dimuat.
 */
export function ResetPasswordScreen({ onDone }: { readonly onDone: () => void }): ReactNode {
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [sessionReady, setSessionReady] = useState<boolean | null>(null);

  useEffect(() => {
    let aktif = true;
    /*
     * Sesi pemulihan dibentuk Supabase dari tautan, dan itu TIDAK seketika — pada alur PKCE ia
     * menukar `code` lewat jaringan lebih dulu. Menanyakan sesi sekali lalu menyerah akan menolak
     * tautan yang sebenarnya sah, dan orangnya akan mengira emailnya rusak.
     */
    const { data: langganan } = supabase.auth.onAuthStateChange((_event, session) => {
      if (aktif && session) setSessionReady(true);
    });
    void supabase.auth.getSession().then(({ data }) => {
      if (aktif) setSessionReady(data.session !== null);
    });
    return () => {
      aktif = false;
      langganan.subscription.unsubscribe();
    };
  }, []);

  async function simpan(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    const masalah = passwordProblem(password, confirmation);
    if (masalah) {
      setError(masalah);
      return;
    }
    setBusy(true);
    setError(null);
    const { error: gagal } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (gagal) {
      // Pesan Supabase apa adanya: "kata sandi terlalu lemah" dan "tautan kedaluwarsa" menuntut
      // tindakan yang berbeda, dan menyeragamkannya membuat orang mengulang hal yang salah.
      setError(gagal.message);
      return;
    }
    setDone(true);
  }

  if (done) {
    return (
      <section className="card">
        <h2>Kata sandi tersimpan</h2>
        <p className="muted small">Sekarang masuk memakai kata sandi baru Anda.</p>
        <button type="button" className="button-primary" onClick={onDone}>
          Ke layar masuk
        </button>
      </section>
    );
  }

  return (
    <form className="card" onSubmit={(e) => void simpan(e)}>
      <h2>Kata sandi baru</h2>

      {sessionReady === false ? (
        <p className="warning">
          Tautan ini sudah tidak berlaku — biasanya karena kedaluwarsa atau sudah dipakai. Minta
          tautan baru dari layar masuk.
        </p>
      ) : null}

      {error ? <p role="alert">{error}</p> : null}

      <label htmlFor="pwd-baru">Kata sandi baru</label>
      <IsianSandi
        id="pwd-baru"
        value={password}
        autoComplete="new-password"
        onChange={(e) => setPassword(e.target.value)}
      />

      <label htmlFor="pwd-ulang">Ulangi kata sandi baru</label>
      <IsianSandi
        id="pwd-ulang"
        value={confirmation}
        autoComplete="new-password"
        onChange={(e) => setConfirmation(e.target.value)}
      />

      <button type="submit" className="button-primary" disabled={busy || sessionReady === false}>
        {busy ? 'Menyimpan…' : 'Simpan kata sandi'}
      </button>
      <button type="button" className="link-quiet" onClick={onDone}>
        Kembali ke layar masuk
      </button>
    </form>
  );
}
